import { apiFetch } from './api';
import type { Difficulty } from '../types';
import type { Badge } from './badges';

export interface DifficultyStats {
  solves: number;
  bestMs: number | null;
}
export interface GameStats {
  streak: number;
  longestStreak: number;
  totalSolves: number;
  totalDaysSolved: number;
  byDifficulty: Record<Difficulty, DifficultyStats>;
  todaySolved: Record<Difficulty, boolean>;
}

interface RecordSolveBody {
  puzzleDate: string;   // YYYY-MM-DD in America/New_York (matches the generator's seeding date)
  difficulty: Difficulty;
  elapsedMs: number;
}

export interface RecordSolveResult {
  stats: GameStats;
  /// Badges the server granted on this solve. Empty when nothing new
  /// was unlocked. The play surface uses these to render an achievement
  /// popup over the win screen.
  newBadges: Badge[];
}

export async function recordSolve(body: RecordSolveBody): Promise<RecordSolveResult | null> {
  const res = await apiFetch('/solves', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return {
    stats: data.stats as GameStats,
    newBadges: Array.isArray(data.newBadges) ? (data.newBadges as Badge[]) : [],
  };
}

export async function fetchStats(): Promise<GameStats | null> {
  const res = await apiFetch('/solves/me/stats');
  if (!res.ok) return null;
  const data = await res.json();
  return data.stats as GameStats;
}

export interface SolveHistoryEntry {
  puzzleDate: string;
  difficulty: Difficulty;
  elapsedMs: number;
  solvedAt: string;
}

/// Pulls the user's entire solve history (server caps at 365 rows).
/// Powers the archive view's per-row completion indicators. Returns
/// `null` when unauthenticated or the route errors — caller treats nil
/// as "no completed puzzles."
export async function fetchSolveHistory(): Promise<SolveHistoryEntry[] | null> {
  const res = await apiFetch('/solves/me/solves?limit=365');
  if (!res.ok) return null;
  const data = await res.json();
  return data.solves as SolveHistoryEntry[];
}

// ─────────────────────────────────────────────────────────────────────
// Daily leaderboard — top times for one (date, difficulty). Public read;
// when signed-in the viewer's row is flagged with isMe and their rank
// surfaced via viewerRank. Mirrors the shape of the custom-puzzle
// leaderboard (fetchLeaderboard in customPuzzles.ts) so the UI layer
// is interchangeable.
// ─────────────────────────────────────────────────────────────────────

export interface DailyLeaderboardEntry {
  displayName: string;
  elapsedMs: number;
  solvedAt: string;
  /// True for the signed-in viewer's own row. Used to highlight in the
  /// modal table. Always false for anonymous viewers.
  isMe: boolean;
}

export interface DailyLeaderboardResponse {
  date: string;
  difficulty: Difficulty;
  /// 'all' = global leaderboard. 'friends' = filtered to viewer + the
  /// users they follow. Server downgrades 'friends' → 'all' when the
  /// viewer is anonymous (no friend graph).
  scope?: 'all' | 'friends';
  entries: DailyLeaderboardEntry[];
  /// 1-indexed rank of the signed-in viewer's solve for this (date,
  /// difficulty), counting strictly-faster solves only. Null when the
  /// viewer hasn't solved this puzzle (or is anonymous).
  viewerRank: number | null;
  /// Total signed-in solves for this (date, difficulty), regardless of
  /// the row limit. Surfaced in the modal footer.
  totalSolves: number;
}

export async function fetchDailyLeaderboard(
  date: string,
  difficulty: Difficulty,
  limit = 25,
  scope: 'all' | 'friends' = 'all',
): Promise<DailyLeaderboardResponse | null> {
  try {
    const q = new URLSearchParams({ date, difficulty, limit: String(limit) });
    if (scope === 'friends') q.set('friends', '1');
    const res = await apiFetch(`/solves/leaderboard?${q.toString()}`);
    if (!res.ok) return null;
    return await res.json() as DailyLeaderboardResponse;
  } catch { return null; }
}
